import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { PermissionsGate } from '@components';
import { ModalFooter } from 'components/common/modals';
import { ButtonV2 } from 'components/common/ui';
import { FieldV2, Label } from 'components/common/form-controls';
import { showError } from '@utils';

import { AVAILABLE_VIDEO_TAGS } from 'hooks/videos/utils';
import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import validationSchema from './validationSchema';
import styles from './videoForm.style';

const getPayload = (values = {}) => {
  return {
    skus: values.skus ? values.skus.split(',').map((sku) => sku.trim()) : [],
    tags: values.tags,
    vimeoUrl: values.vimeoUrl,
    order: values.id ? Number(values.order) : null,
    title: values.title,
    published: values.published,
    thumbnailUrl: values.thumbnailUrl,
    buttonRedirectionUrl: values.buttonRedirectionUrl,
    buttonTextGr: values.buttonTextGr,
    buttonTextEn: values.buttonTextEn,
  };
};

const VideoForm = ({
  defaultValues = {},
  isFieldsEditable = true,
  onSubmit,
  onEdit,
  onDelete,
}) => {
  const resolver = yupResolver(validationSchema);
  const methods = useForm({ defaultValues, resolver });
  const vimeoUrl = methods.watch('vimeoUrl');
  const tags = methods.watch('tags');
  const order = methods.watch('order');
  const id = methods.watch('id');
  const thumbnailUrl = methods.watch('thumbnailUrl');
  const setTitle = (title = '') => {
    methods.setValue('title', title);
  };

  const setThumbnailUrl = (thumbnailUrlValue = '') => {
    methods.setValue('thumbnailUrl', thumbnailUrlValue);
  };

  const getVimeoVideoData = async (vimeoUrlValue) => {
    try {
      const apiUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
        vimeoUrlValue
      )}`;
      const response = await fetch(apiUrl);
      const data = await response.json();
      setTitle(data.title);
      setThumbnailUrl(data.thumbnail_url);

      return data; // Extract the video title
    } catch (error) {
      showError('Error fetching Vimeo title:', error);
      return null;
    }
  };

  const handleBlurVideoUrl = () => {
    if (vimeoUrl) {
      getVimeoVideoData(vimeoUrl);
    }
  };

  const handleChangeTag = (value, name) => {
    if (tags.includes(name)) {
      methods.setValue(
        'tags',
        tags.filter((tag) => tag !== name)
      );
    } else if (!tags.includes(name)) {
      methods.setValue('tags', [...tags, name]);
    }
    methods.setValue(name, value);
    if (name === 'available_in_car_page' && !value) {
      methods.setValue('skus', '');
    }
  };

  useEffect(() => {
    if (defaultValues) {
      methods.reset(defaultValues);
    }
  }, [defaultValues, methods]);

  const handleSubmit = (values) => {
    const payload = getPayload(values);
    onSubmit(payload);
  };

  const showEditIcon = id && onEdit;

  return (
    <FormProvider {...methods}>
      <form
        id={`video-form-${defaultValues.id}`}
        onSubmit={methods.handleSubmit(handleSubmit)}
      >
        <Box sx={styles.topWrapper}>
          {id && <Box sx={styles.order}>{order}</Box>}
          {thumbnailUrl && (
            <Box>
              <Box
                sx={{
                  backgroundImage: `url('${thumbnailUrl}')`,
                  ...styles.image,
                }}
              />
              <Label>Video Preview</Label>
            </Box>
          )}
          {showEditIcon && (
            <PermissionsGate entity={ENTITIES_PERMISIONS.WriteVideos}>
              <EditIcon
                data-testid="EditIcon"
                onClick={onEdit}
                sx={styles.edit}
              />
              <DeleteIcon
                data-testid="DeleteIcon"
                onClick={onDelete}
                sx={styles.delete}
              />
            </PermissionsGate>
          )}
        </Box>
        <FieldV2
          name="vimeoUrl"
          isRequired
          control={methods.control}
          label="Video URL"
          onBlur={handleBlurVideoUrl}
          disabled={!isFieldsEditable}
        />
        <FieldV2
          name="title"
          control={methods.control}
          disabled
          label="Title"
        />
        {defaultValues.id && (
          <FieldV2
            name="order"
            control={methods.control}
            type="number"
            label="Order"
            disabled={!isFieldsEditable}
            min="0"
          />
        )}

        <FieldV2
          name="available_in_car_page"
          control={methods.control}
          type="checkbox"
          label={AVAILABLE_VIDEO_TAGS.available_in_car_page}
          wrapperStyle={styles.checkboxField}
          onChange={(value) => handleChangeTag(value, 'available_in_car_page')}
          disabled={!isFieldsEditable}
        />
        <FieldV2
          name="available_in_car_list"
          control={methods.control}
          type="checkbox"
          label={AVAILABLE_VIDEO_TAGS.available_in_car_list}
          wrapperStyle={styles.checkboxField}
          onChange={(value) => handleChangeTag(value, 'available_in_car_list')}
          disabled={!isFieldsEditable}
        />
        <FieldV2
          name="available_in_home_page"
          control={methods.control}
          type="checkbox"
          label={AVAILABLE_VIDEO_TAGS.available_in_home_page}
          wrapperStyle={styles.checkboxField}
          onChange={(value) => handleChangeTag(value, 'available_in_home_page')}
          disabled={!isFieldsEditable}
        />

        <FieldV2
          name="published"
          control={methods.control}
          type="checkbox"
          label="Published"
          wrapperStyle={styles.checkboxField}
          disabled={!isFieldsEditable}
        />
        <FieldV2
          name="skus"
          control={methods.control}
          label="Visible SKUs on video (comma separated)"
          disabled={
            !isFieldsEditable || !tags.includes('available_in_car_page')
          }
        />
        <FieldV2
          name="buttonRedirectionUrl"
          control={methods.control}
          label="Redirect Url"
          disabled={!isFieldsEditable}
        />
        <FieldV2
          name="buttonTextGr"
          control={methods.control}
          label="Button text GR"
          disabled={!isFieldsEditable}
        />
        <FieldV2
          name="buttonTextEn"
          control={methods.control}
          label="Button text EN"
          disabled={!isFieldsEditable}
        />

        {isFieldsEditable && (
          <ModalFooter>
            <ButtonV2 type="submit" disabled={methods.formState.isSubmitting}>
              Save
            </ButtonV2>
          </ModalFooter>
        )}
      </form>
    </FormProvider>
  );
};

export default VideoForm;
